// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import React, {Component} from 'react';
import DownloadOnMobileScss from './DownloadOnMobile.scss';
import Button from '@react/react-spectrum/Button';
import uuid from 'uuid';
import Close from '@react/react-spectrum/Icon/Close';
import AppsUtil from '../../utils/AppsUtil';
import DownloadUtil from '../../utils/DownloadUtil';
import CCDUtil from '../../utils/CCDUtil';
import {ipadWithStoreLink, genericEmailMeLink, chromeBookCompanionMobileApp, chromeBookAlternateMobileApp, hasCompanionApp, chromeBookAlternateAppName} from './AppMobileData';
import CCDIngestAnalytics from '../../analytics/CCDIngestAnalytics';
import {mobileData, getContextGuid, mobileClickData, sendIngestLoggedOutPromotionExpEvent} from '../../utils/CCDAnalyticsUtils';
import {analytics, errorTypes} from '../../constants/ccdConstants';
import Url from '../../utils/Url';

type Props = {
	routeParams: Object,
	process: string,
	onAppDetailEvent?: Function,
	closePDP?: Function,
	formatMessage: Function,
	locale: string,
	environment: string,
	closeDialog?: Function,
	appId?: string
};

export default class DownloadOnMobile extends Component <Props> {
	productDetails: Object;
	showTabletPage: boolean;

	constructor(props: Props) {
		super(props);
		this.showTabletPage = false;
		if (this.props.process === 'cchome') {
			const productId = AppsUtil.getCCWebPathData().product;
			const productData = AppsUtil.getFFCProductConfig(productId);
			this.productDetails = {
				name: productData.displayName,
				id: AppsUtil.getProductIdFromSapcode(productData.id),
				sapCode: productData.id,
				productIconUrl: AppsUtil.getProductIcon(productData)
			};
		}
		if (this.props.process === 'ccd') {
			this.productDetails = CCDUtil.getProductsData(this.props.routeParams.productId);
			this.productDetails.productIconUrl = CCDUtil.getProductIconURl(this.productDetails.id);
		}
		this.productDetails.bannerImageUrl = CCDUtil.getUrlPath(`/assets/ccd/images/mobile_page/${this.productDetails.id}.jpg`);
	}

	componentWillMount() {
		const branchURL = AppsUtil.getCompanionAppBranchURL(this.productDetails.id);
		const storeLink = branchURL;
		if ((AppsUtil.isIPad() && this.productDetails.sapCode === 'PHSP')
			|| (AppsUtil.isChromeOS() && storeLink)) {
			this.showTabletPage = true;
		}
		this.sendAnalytic(analytics.mobileScreen);
	}

	sendAnalytic(type: string) {
		const ccdIngestAnalytics = new CCDIngestAnalytics(this.props.environment);
		ccdIngestAnalytics.initAnalytics();

		let data = {};
		switch (type) {
			case 'mobileScreen':
				data = CCDUtil.getCloneObject(mobileData);
				break;
			case 'sendMeLinkClicked' :
				data = CCDUtil.getCloneObject(mobileClickData);
				data.subType = 'send-link';
				break;
			case 'googlePlayClicked' :
				data = CCDUtil.getCloneObject(mobileClickData);
				data.subType = 'google-play';
				break;
		}
		if (this.showTabletPage) {
			data.subCategory = 'Downloading Tablet Incompatible';
			data.errorType = AppsUtil.isIPad() ? 'iPad' : 'Chromebook';
		}
		data.productId = this.productDetails.id;
		if (this.props.process === 'cchome') {
			data.workflow = 'Apps';
			data.productName = this.productDetails.name;
		}
		data.componentName = 'Kaizen';
		data.contextGuid = getContextGuid(true, false, false);
		ccdIngestAnalytics.genericAnalyticEvent(data);
	}


	onClickButton() {
		// this.props.onAppDetailEvent('click', 'send-link', downloadUrl, guid);this.props.closeDialog();
		this.sendAnalytic(analytics.sendMeLinkClicked);
		// Send legacy Ingest email me a link button click event for promotion workflow
		if (AppsUtil.isLoggedOutPromotionExp) {
			sendIngestLoggedOutPromotionExpEvent('email-me-link-button-clicked');
		}
	}

	getStoreIcon(storeName: string) {
		if (!this.isCompanionAppDataAvailable()) {
			return null;
		}
		const branchURL = AppsUtil.getCompanionAppBranchURL(this.productDetails.id);
		const storeLink = branchURL;
		if (storeName === 'ipad' || storeName === 'iphone') {
			return <a aria-label={this.props.formatMessage({id: 'apps.downloadOnMobile.getOnIosAriaLabel'}, {appName: this.productDetails.name})} target='_blank' href={storeLink}>
				<img className={DownloadOnMobileScss.iosImage} alt={this.props.formatMessage({id: 'apps.tile.ios'})} title={this.props.formatMessage({id: 'apps.tile.ios'})} src={`/assets/apps/mobile_badges/${this.props.locale}/ios-badge.svg`}/>
			</a>;
		}
		if (storeName === 'android') {
			return <a aria-label={this.props.formatMessage({id: 'apps.downloadOnMobile.getOnAndroidAriaLabel'}, {appName: this.productDetails.name})} target='_blank' href={storeLink}>
				<img className={DownloadOnMobileScss.androidImage} alt={this.props.formatMessage({id: 'apps.tile.android'})} title={this.props.formatMessage({id: 'apps.tile.android'})} src={`/assets/apps/mobile_badges/${this.props.locale}/android-badge.png`} onClick={() => this.sendAnalytic('googlePlayClicked')}/>
			</a>;
		}
	}

	isCompanionAppDataAvailable() {
		return !(this.props.errorType === errorTypes.companionAppDataUnavailable);
	}

	renderEmailMeLinkComponent(type: string, linkTextId: string) {
		const guid = uuid.v4();
		const buttonText = this.props.formatMessage({id: linkTextId});
		const emailHref = DownloadUtil.getEmailMeLinkHref(
			this.productDetails.name === 'creative-cloud',
			this.productDetails.name,
			this.props.appId,
			this.props.process,
			this.productDetails.downloadURL,
			guid,
			this.props.formatMessage
		);
		if (type === 'link') {
			return (
				<a href={emailHref}
					onClick={() => {this.onClickButton();}}>
					{this.props.formatMessage({id: linkTextId})}
				</a>
			);
		}
		return (
			<Button element='a' href={emailHref}
				variant='cta' className={`${DownloadOnMobileScss.download_button} tileButton`} onClick={() => {this.onClickButton();} }label={buttonText}>
			</Button>
		);
	}

	isAppRedirectFromPhotoPlan() {
		// return true;
		return (this.productDetails.sapCode === 'LTRM' || this.productDetails.sapCode === 'LRCC') && (Url.getParam('isAppPicker') && ['ccpp', 'ccppn'].includes(Url.getParam('isAppPicker').toLowerCase()));
	}

	renderBody() {
		const bodyElemArray = [];
		const branchURL = AppsUtil.getCompanionAppBranchURL(this.productDetails.id);
		const storeLink = branchURL;
		const productsWithIpadCompanionAppWorkflow = ['PHSP', 'ILST'];
		if ((AppsUtil.isIPad() && productsWithIpadCompanionAppWorkflow.includes(this.productDetails.sapCode))
			|| (AppsUtil.isChromeOS() && !this.isCompanionAppDataAvailable())) { // iPad workflow for Photoshop only
			this.isCompanionAppDataAvailable() && bodyElemArray.push(<p>{this.props.formatMessage({id: ipadWithStoreLink.messageIdsFirstPara}, {appName: this.productDetails.name})}</p>);
			bodyElemArray.push(this.getStoreIcon('ipad'));
			bodyElemArray.push(<p>{this.props.formatMessage({id: ipadWithStoreLink.messageIdsSecondPara}, {appName: this.productDetails.name})}</p>);
			bodyElemArray.push(this.renderEmailMeLinkComponent('link', ipadWithStoreLink.emailMelink));
		}
		else if (AppsUtil.isChromeOS() && storeLink) { // chromeOs workflow = chromebook for products with companion mobile app
			const paraMessageId = hasCompanionApp(this.productDetails.id) ? chromeBookCompanionMobileApp.messageIdsFirstPara : chromeBookAlternateMobileApp.messageIdsFirstPara;
			bodyElemArray.push(<p className={DownloadOnMobileScss.chromeBookBody}>{this.props.formatMessage({id: paraMessageId}, {appName: chromeBookAlternateAppName[this.productDetails.id] ? chromeBookAlternateAppName[this.productDetails.id] : this.productDetails.name, br : () => <br/>})}</p>);
			bodyElemArray.push(this.getStoreIcon('android'));
		}
		else { // iPhone & Android devices other than ChromeOs workflow
			bodyElemArray.push(<p>{this.props.formatMessage({id: genericEmailMeLink.messageIdsFirstPara}, {appName: this.productDetails.name})}</p>);
			bodyElemArray.push(this.renderEmailMeLinkComponent('button', genericEmailMeLink.emailMelink));
		}
		return bodyElemArray;
	}

	renderMobileDownloadHeader() {
		if (this.props.process === 'cchome') {
			return <div className={DownloadOnMobileScss.header}>
				<Close size='M' className={DownloadOnMobileScss.close} onClick={(e) => {
					e.preventDefault();
					this.props.closeDialog && this.props.closeDialog();
					this.props.onAppDetailEvent && this.props.onAppDetailEvent('click', 'close');
				}}/>
			</div>;
		}
		return null;
	}

	render() {
		return (
			<div className={DownloadOnMobileScss.container}>
				{this.renderMobileDownloadHeader()}
				<div className={DownloadOnMobileScss.imageContainer}>
					{!AppsUtil.isChromeOS() && <img className={DownloadOnMobileScss.bannerImage} alt={this.productDetails.name} src={this.productDetails.bannerImageUrl}/>}
					<div>
						<img className={`${DownloadOnMobileScss.productIcon}${AppsUtil.isChromeOS() ? ' remove-negative-margin-top' : ''}`} alt={this.productDetails.name} src={this.productDetails.productIconUrl}/>
					</div>
				</div>
				<div className={DownloadOnMobileScss.dataContainer}>
					<div className={DownloadOnMobileScss.title}>{this.productDetails.name}</div>
					<div className={DownloadOnMobileScss.body}>{this.renderBody()}</div>
				</div>
			</div>
		);
	}
}
